// Edit planner for CREATE / ALTER / DROP {PROCEDURE|FUNCTION} (v2 Slice D7).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice D7.
//
// All three ops append a fresh statement after the source statement
// (DSE2-7: no coalescing by default). The source statement kind is
// unrestricted — each op names its target via its own fields.

import { rangeFromOffsets } from '../../../sql-grammar/document/lineIndex';
import type { DdlEdit, PlannedByteEdit } from '../../types';

import { pickAppendInsertion } from './appendInsertion';
import type { DdlEditCtx } from './applyDdlEdit';
import {
  synthesizeAlterRoutine,
  synthesizeCreateRoutine,
  synthesizeDropRoutine,
} from './dialect.routine';

type RoutineEdit = Extract<
  DdlEdit,
  { op: 'create-routine' | 'alter-routine' | 'drop-routine' }
>;

export function planRoutineEdit(
  ctx: DdlEditCtx,
  edit: RoutineEdit,
): readonly PlannedByteEdit[] {
  const synth = synthForRoutine(ctx, edit);
  const { offset: insertAt, sep, trailSep } = pickAppendInsertion(
    ctx.source,
    ctx.statementInfo.range.endOffset,
  );
  return [{
    range: rangeFromOffsets(ctx.lineIdx, insertAt, insertAt),
    replacement: sep + synth + trailSep,
    rationale: edit.op,
  }];
}

function synthForRoutine(ctx: DdlEditCtx, edit: RoutineEdit): string {
  switch (edit.op) {
    case 'create-routine':
      return synthesizeCreateRoutine(ctx.engine, edit.routine);
    case 'alter-routine':
      return synthesizeAlterRoutine(
        ctx.engine,
        edit.routineName,
        edit.routineKind,
        edit.change,
      );
    case 'drop-routine':
      return synthesizeDropRoutine(
        ctx.engine,
        edit.routineName,
        edit.routineKind,
        edit.ifExists ?? false,
        edit.cascade ?? false,
        edit.pgsqlSignature,
      );
  }
}
